import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { removePii } from "@asurion-core/expert-workspace-pii";
import { trackFullStoryEvent } from "@expert/monitoring";
import { type DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useEffect, useState } from "react";

import type { ExpertAssistMessage } from "../../shared-types";

import classes from "./FeedbackIcons.module.css";
import thumbsDownLottieSrc from "./thumbsDownLottie.json?url";
import thumbsUpLottieSrc from "./thumbsUpLottie.json?url";

interface FeedbackIconProps {
    onFeedbackClick: () => void;
    animationType: "up" | "down";
    message: ExpertAssistMessage | undefined;
}
function FeedbackIcons({ onFeedbackClick, animationType, message }: FeedbackIconProps) {
    const animationSrc = animationType === "up" ? thumbsUpLottieSrc : thumbsDownLottieSrc;
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
    // The frame range to play. Starts on the intro segment; hover/click swap it. The latest value always
    // wins, so an interaction before the animation has loaded plays the right segment (not the intro).
    const [activeSegment, setActiveSegment] = useState<[number, number]>([1, 80]);
    const { dispatcher } = useReactAnalytics();

    // Play the active segment. `isLoaded` (not `isReady`) tells us this animation's data is ready — the WASM
    // core can be ready while the JSON is still loading — so if it isn't loaded yet we play once it is.
    useEffect(() => {
        if (!dotLottie) return;
        const play = () => {
            dotLottie.setSegment(activeSegment[0], activeSegment[1]);
            // Seek to the segment start: play() resumes from the current frame, so replaying the same
            // segment (e.g. a repeated hover) would otherwise sit at its end and not visibly animate.
            dotLottie.setFrame(activeSegment[0]);
            dotLottie.play();
        };
        if (dotLottie.isLoaded) {
            play();
            return;
        }
        dotLottie.addEventListener("load", play);
        return () => dotLottie.removeEventListener("load", play);
    }, [dotLottie, activeSegment]);

    const handleMouseEnter = () => setActiveSegment([90, 160]);

    const handleClick = () => {
        setActiveSegment([192, 292]);
        const text = message ? message.text.slice(0, 100) : "";
        const button = animationType === "up" ? "FeedbackThumbsUp" : "FeedbackThumbsDown";
        const sessionType = message?.metadata?.sessionSummary?.sessionType;
        const previousSessionId = message?.metadata?.sessionSummary?.previousSessionId;

        void removePii(text).then((piiScrubberText) => {
            void dispatcher
                .withMeta({
                    SkipEDP: false,
                })
                .dispatchAction("Click", "MessageFeedbackDetails", {
                    button,
                    messageId: message?.id,
                    text: piiScrubberText,
                    messageType: message?.type,
                    sessionType,
                    previousSessionId,
                });
        });
        trackFullStoryEvent("Click_MessageFeedbackDetails", {
            button,
            messageId: message?.id,
            messageType: message?.type,
        });
        setTimeout(() => {
            onFeedbackClick();
        }, 1000);
    };

    return (
        <DotLottieReact
            dotLottieRefCallback={setDotLottie}
            src={animationSrc}
            autoplay={false}
            loop={false}
            className={classes.player}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
        />
    );
}

export default FeedbackIcons;
